import { AssessmentService,PortfolioService } from '@amzn/awscat-portfolio-assessment-service-client';
import { Tag } from '@amzn/awscat-portfolio-assessment-types';
import { CustomerAccount, PortfolioToCreate } from '@amzn/awscat-portfolio-assessment-types';
import { AuthContextInterface, FlashContextInterface, FlashType, withAuthContext, withFlashContext } from '@amzn/awscat-react-components';
import { Button, Container, Form, FormField, Header, Input, NonCancelableCustomEvent, SpaceBetween, Tiles, TilesProps } from '@amzn/awsui-components-react';
import { FunctionComponent, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router';

import { HelpPanelTopicOptions } from '../../common/Constants';
import { constants } from '../../common/Localization';
import { isAWSUser } from '../../common/util';
import { HelpPanelContext } from '../../contexts/HelpPanelContext';
import { classificationItems, ClassificationTypes, prepopulateItems, testAccountIDs } from './constants';
import CustomerAccountSettings from './CustomerAccountSettings';
import { OrgTypes, PrepopulateTypes } from './interface';
import PortfolioTagEditor from './PortfolioTagEditor';
type Props = FlashContextInterface & AuthContextInterface;
const portfolioService = new PortfolioService();
const assessmentService = new AssessmentService();

function isTest(prepopulate: string, classification: string): boolean {
    return prepopulate === PrepopulateTypes.SAMPLE || classification === ClassificationTypes.TEST;
}

const CreatePortfolio: FunctionComponent<Props> = ({flash, auth}): JSX.Element => {
    const { panelHandler } = useContext(HelpPanelContext);
    const [portfolioValues, setPortfolioValues] = useState({
        portfolioName: '',
        classification: ClassificationTypes.CUSTOMER,
        prepopulate: PrepopulateTypes.EMPTY,
        tags: new Array<Tag>(),
    });
    const [submitDisabled, setSubmitDisabled] = useState(true);
    const [loading, setLoading] = useState(false);
    const [account, setAccount] = useState({
        refId: '',
        accountId: '',
        accountName: '',
        ownerId: '',
        territory: '',
    } as CustomerAccount);


    function onChange(event: NonCancelableCustomEvent<any>, propertyName: string) {
        setPortfolioValues({...portfolioValues, [propertyName]: event.detail.value}); 
    }

    useEffect(() => {
        panelHandler(HelpPanelTopicOptions.CREATE_PORTFOLIO);
    }, [ panelHandler ]);

    useEffect(() => {
        function validate(): boolean {
            if (isTest(portfolioValues.prepopulate, portfolioValues.classification)) {
                return !!portfolioValues.portfolioName;
            }
            if (isAWSUser(auth)) {
                return !!portfolioValues.portfolioName && !!account?.refId && !!account?.accountId;
            } else {
                return !!portfolioValues.portfolioName && !!account?.accountName;
            }
        }
        const isFormValid = validate();
        setSubmitDisabled(!isFormValid);
    }, [portfolioValues, account, auth]);

    const PortfolioSettings = (
        <Container header={<Header variant="h2">{constants.CREATE_PORTFOLIO_SETTINGS_HEADER}</Header>}>
            <FormField
                description={constants.CREATE_PORTFOLIO_NAME_DESP}
                label={constants.CREATE_PORTFOLIO_NAME}>
                <Input
                    placeholder={constants.CREATE_PORTFOLIO_NAME_PLACEHOLDER}
                    value={portfolioValues.portfolioName}
                    onChange={event => onChange(event, 'portfolioName')}
                    disabled={loading}
                />
            </FormField>
        </Container>
    );

    const PortfolioTags = (
        <PortfolioTagEditor 
            tags={portfolioValues.tags}
            setTags={(tags: Tag[]) => setPortfolioValues({...portfolioValues, tags})}
            updating={loading} />
    );

    function onPrepopulateChange(event: NonCancelableCustomEvent<TilesProps.ChangeDetail>) {
        const prepopulate = event.detail.value as PrepopulateTypes;
        classificationItems.forEach(item => {
            item.disabled = prepopulate === PrepopulateTypes.SAMPLE;
        });
        const classification = prepopulate ? ClassificationTypes.TEST: portfolioValues.classification;
        setPortfolioValues({...portfolioValues, prepopulate, classification});
    }
    const PortfolioData = (
        <Container header={<Header variant="h2">{constants.CREATE_PORTFOLIO_DATA_HEADER}</Header>}>
            <SpaceBetween size="l">
                <FormField
                    description={constants.CREATE_PORTFOLIO_PREPOP_DESP}
                    label={constants.CREATE_PORTFOLIO_PREPOP_LABEL}>
                    <Tiles
                        value={portfolioValues.prepopulate}
                        items={prepopulateItems}
                        onChange={onPrepopulateChange}
                    />
                </FormField>
                <FormField
                    description={constants.CREATE_PORTFOLIO_CLASS_DESP}
                    label={constants.CREATE_PORTFOLIO_CLASS_LABEL}>
                    <Tiles
                        value={portfolioValues.classification}
                        items={classificationItems}
                        onChange={event => onChange(event, 'classification')}
                    />
                </FormField>
            </SpaceBetween>
        </Container>
    );

    const history = useHistory();

    async function onSubmit() {
        setLoading(true);
        const { prepopulate, classification, portfolioName, tags } = portfolioValues;
        const isTestPortfolio = isTest(prepopulate, classification);
        let customerAccountId: string = account.accountId;
        if (classification === ClassificationTypes.TEST) {
            const orgType = isAWSUser(auth) ? OrgTypes.AWS : OrgTypes.other;
            customerAccountId = testAccountIDs[orgType][prepopulate];
        }
        const portfolioToCreate: PortfolioToCreate = {
            customerAccountId,
            name: portfolioName,
            accountName: account.accountName,
            isTestPortfolio,
            refId: account.refId,
            salesForceAccountId: account.refId,
            cloneDemoPortfolio: prepopulate === PrepopulateTypes.SAMPLE,
            tags,
        };

        try {
            const portfolio = await portfolioService.create(portfolioToCreate);
            const assessment = await assessmentService.create({customerAccountId: account.accountId, portfolioId: portfolio.id});
            await portfolioService.update({ id: portfolio.id, assessmentId: assessment.id});
            setLoading(false);
            history.push(`portfolio/${portfolio.id}/management/assets/servers`);
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.CREATE_PORTFOLIO_ERROR);
        }
    }

    return (
        <form onSubmit={e => e.preventDefault()}>
            <Form
                header={
                    <Header variant="h1" description={constants.CREATE_PORTFOLIO_DESCRIPTION}>
                        {constants.CREATE_A_NEW_PORTFOLIO}
                    </Header>
                }
                actions={
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button variant="link" loading={loading} onClick={() => {history.push('/portfolios');}}>{constants.CANCEL_BUTTON_TEXT}</Button>
                        <Button variant="primary" disabled={submitDisabled} loading={loading} onClick={onSubmit}>{constants.CREATE_A_NEW_PORTFOLIO}</Button>
                    </SpaceBetween>
                }
            >
                <SpaceBetween size='l'>
                    {PortfolioSettings}
                    {PortfolioData}
                    {isTest(portfolioValues.prepopulate, portfolioValues.classification) ? null: CustomerAccountSettings({account, setAccount})}
                    {PortfolioTags}
                </SpaceBetween>
            </Form>
        </form>
    );
};

export default withAuthContext(withFlashContext(CreatePortfolio));
